// src/components/dashboard/AssignReviewerModal.jsx
//
// Reviewer picker for the Review Queue.
//
// Shows every reviewer with their current open workload, so a lead reviewer
// can spread work rather than repeatedly picking the first name in the list.
// The count comes from /api/admin/reviewers (activeReviews) and covers open
// drafts AND covers -- a reviewer buried in cover work is not free, and a
// drafts-only number would say they were.

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Avatar, Chip, Typography, Box, IconButton,
  List, ListItemButton, ListItemAvatar, ListItemText, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/** "J. Alvarez" -> "JA". Falls back to the first character for a single word. */
function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/);
  return parts.map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

/**
 * Colour the workload chip by how loaded the reviewer is. Thresholds are
 * deliberately blunt -- this is a nudge, not a scheduling algorithm.
 */
function loadColor(n) {
  if (n === 0) return 'success';
  if (n <= 2) return 'default';
  return 'warning';
}

export default function AssignReviewerModal({
  open,
  draft,
  reviewers,
  busy,
  onAssign,
  onUnassign,
  onClose,
}) {
  const currentId = draft?.assignedTo || null;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {currentId ? 'Reassign reviewer' : 'Assign reviewer'}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {draft ? `${draft.title || 'Untitled'}${draft.genre ? ` — ${draft.genre}` : ''}` : ''}
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {reviewers.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No reviewers available.
          </Typography>
        ) : (
          <List disablePadding>
            {reviewers.map((r) => {
              const isCurrent = String(r.id) === String(currentId);
              const count = Number(r.activeReviews ?? 0);
              return (
                <ListItemButton
                  key={r.id}
                  // One click assigns -- there is no confirm step. Reassigning is
                  // cheap and reversible, and a second dialog to confirm a triage
                  // click is friction with nothing behind it.
                  onClick={() => onAssign(r.id)}
                  disabled={busy}
                  selected={isCurrent}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 13 }}>
                      {initials(r.username)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={r.username}
                    secondary={isCurrent ? 'Currently assigned' : null}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    color={loadColor(count)}
                    label={count === 1 ? '1 active review' : `${count} active reviews`}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
        {busy && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Saving…</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {currentId && (
          <Button onClick={onUnassign} disabled={busy} color="inherit">
            Unassign
          </Button>
        )}
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
